import { Location } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2, inject, DOCUMENT } from '@angular/core';
import { MetaDefinition, Meta as MetaService } from '@angular/platform-browser';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { DfApiConfigService } from '@df/api/config/df-api-config.service';
import { IModelDataV3, IModelDataV3WithUrl, Model } from '@df/catalog/model';
import { Route } from '@df/catalog/route/route';
import { RouteResolver } from '@df/catalog/route/route.resolver';
import { App } from '@df/core/app';
import { HTTPStatus, MetaState } from '@df/core/meta/meta-state';
import { MetaStateFactory } from '@df/core/meta/meta-state.factory';
import { StructuralData } from '@df/core/meta/structural-data/structural-data.namespace';
import { Client } from '@df/core/service/client';
import { DfEventService } from '@df/core/service/df-event';
import { NgInject } from '@df/ng/ng-inject';
import { TimeoutService } from '@df/ng/timeout.service';
import difference from 'lodash-es/difference';
import { Subject } from 'rxjs';
import { NCore } from '../df-core.interfaces';

export * from '@df/core/meta/meta-state';

declare module '@df/core/service/df-event' {
    interface IEventType {
        /**
         * Any GTM event pushed to data layer
         */
        dfViewModelChange: DfEventConfig<
            'dfViewModelChange',
            {
                model: Model | undefined;
            },
            void
        >;
    }
}

@Injectable({
    providedIn: 'root'
})
export class Meta {
    /**
     * Changes notification subject
     */
    readonly subject = new Subject<MetaState | undefined>();

    /**
     * Previous meta state
     */
    previousState?: MetaState;

    /**
     * Current state
     */
    state?: MetaState;

    busy = true;

    /**
     * Metas existing now managed by this script
     */
    private existingTags: string[] = [];

    /**
     * Existing title element
     */
    private existingTitle?: HTMLTitleElement;

    /**
     * Existing canonical element
     */
    private existingCanonical?: HTMLLinkElement;

    /**
     * If refferred has been patched alread
     * we need to patch it to provide last page as referrer
     * and not what lead to the website
     * @type {boolean}
     */
    private referrerPatched = false;
    /**
     * Previous page we visited
     * We will pass it as referrer
     *
     * @type {boolean}
     */
    private referrer?: string;

    /**
     * Filtered router events to ignore hash
     */
    readonly routerEvents = new Subject<NavigationStart | NavigationEnd | NavigationError | NavigationCancel>();
    readonly routerEventsSync = new Subject<NavigationStart | NavigationEnd | NavigationError | NavigationCancel>();

    private _renderer2?: Renderer2;
    private get renderer2(): Renderer2 {
        if (!this._renderer2) {
            this._renderer2 = this.rendererFactory.createRenderer(null, null);
        }
        return this._renderer2;
    }

    currentRoute: Route | undefined;

    // this cannot be async @see https://tomandco.atlassian.net/browse/OBR-2189
    onNext = this.timeoutService.debounce(() => {
        if (this.app.data) {
            this._onNext();
        } else {
            this.dfApiConfigService.loadApiConfig().then(() => this.onNext());
        }
    }, 100);

    /**
     * Do not move to constructor
     * we want it only on demand
     */
    @NgInject(RouteResolver)
    private routeResolver!: RouteResolver;

    @NgInject(Location)
    private location!: Location;

    private langSet = false;

    private lastModel?: Model;

    protected dfEventService = inject(DfEventService);
    protected dfCoreOptions = inject(NCore.OPTIONS);

    /**
     * Service initiation
     */
    constructor(
        private metaService: MetaService,
        private router: Router,
        private client: Client,
        private app: App,
        private dfApiConfigService: DfApiConfigService,
        private rendererFactory: RendererFactory2,
        protected metaStateFactory: MetaStateFactory,
        private timeoutService: TimeoutService,
        @Inject(DOCUMENT) readonly document: Document
    ) {
        app.meta = this;

        this.router.events.subscribe(e => this.onRouterChange(e));
        this.subject.subscribe(() => {
            const model = this.model;
            if (this.lastModel !== model) {
                this.lastModel = model;
                this.dfEventService.dispatch({ id: 'dfViewModelChange', data: { model } });
            }
            this.onNext();
        });
    }

    private setLang() {
        if (!this.langSet) {
            this.langSet = true;
            this.dfApiConfigService.loadApiConfig().then(() => {
                const lang = this.app.multisite.current.lang?.find(i => i !== 'x-default');
                if (lang) {
                    this.document.documentElement?.setAttribute('lang', lang);
                }
            });
        }
    }

    /**
     * Subscribe sorthand
     */
    subscribe(...args) {
        return this.subject.subscribe(...args);
    }

    /**
     * Router events listener
     */
    onRouterChange(event): void {
        if (
            !(
                event instanceof NavigationStart ||
                event instanceof NavigationEnd ||
                event instanceof NavigationError ||
                event instanceof NavigationCancel
            )
        ) {
            return;
        }
        this.setLang();
        const path = event.url ? this.routeResolver.cleanUrl(event.url) : undefined;
        // no changes if path hasnt changed
        if (typeof path === 'string' && this.state?.path === path) {
            return;
        }
        if (event instanceof NavigationStart) {
            this.busy = true;
            this.previousState = this.state;
            this.setState(undefined);
            this.currentRoute = undefined;
            this.fixReferrer();
        } else if (event instanceof NavigationEnd) {
            this.busy = false;
            this.nextState(this.routeResolver.cleanUrl(event.url));
        } else if (event instanceof NavigationError) {
            this.applyStateOverride(event.url, 'error');
        }
        this.timeoutService.setTimeout(() => {
            this.routerEvents.next(event);
        });
        this.routerEventsSync.next(event);
    }

    private setState(state: MetaState | undefined): void {
        this.state?.destroy();
        this.state = state;
    }

    updateRoute(route: Route) {
        this.currentRoute = route;

        if (this.state) {
            this.state.route = route;
            this.state.metaData = undefined;
            this.subject.next(this.state);
        }
    }

    applyStateOverride(originalUrl: string, state: string) {
        this.setState(
            this.metaStateFactory.getMetaState(this.routeResolver.cleanUrl(originalUrl), this.currentRoute, undefined, HTTPStatus.error)
        );
        this.busy = false;
        this.subject.next(this.state);
        // show 500 page
        this.router.navigate([state], { skipLocationChange: true });
        if (this.state?.url) {
            this.location.replaceState(this.state.url);
        }
    }

    nextState(url: string) {
        this.setState(this.metaStateFactory.getMetaState(url, this.currentRoute, this.previousState));
        this.busy = false;
        this.subject.next(this.state);
    }

    /**
     * Current status
     */
    get status(): HTTPStatus | undefined {
        if (this.busy) {
            return undefined;
        }
        const state = this.state;
        return state ? state.status : HTTPStatus.error;
    }

    get model(): Model<IModelDataV3WithUrl> | undefined {
        const state = this.state;
        return state ? state.model : undefined;
    }

    set model(model: Model<IModelDataV3WithUrl> | undefined) {
        const state = this.state;
        if (state) {
            if (state.model !== model) {
                state.model = model;
                this.subject.next(state);
            }
        }
    }

    /**
     * Fixes referrer
     */
    private fixReferrer(): void {
        if (typeof document === 'undefined') {
            return;
        }
        // keep previous patch
        if (this.previousState) {
            const url = this.previousState.url.replace(/\~2F/g, '/');
            this.referrer = this.app.url.toUrl(url);
        }
        // -- patching referrer
        // https://plan.tomandco.uk/browse/CWB-384
        if (!this.referrerPatched && this.referrer !== undefined) {
            this.referrerPatched = true;
            Object.defineProperty(document, 'referrer', {
                get: () => this.referrer
            });
        }
    }

    private _onNext() {
        const tags: string[] = [];
        const state = this.state;

        const model = this.model;

        const add = (metaDefinition: MetaDefinition) => {
            if (!metaDefinition.content && !metaDefinition.property) {
                return;
            }
            const tag = this.metaService.updateTag(metaDefinition);
            if (tag && metaDefinition.name) {
                tags.push(metaDefinition.name);
            }
        };

        if (this.client.mode === 'legacyPrerender') {
            add({
                name: 'prerender-status-code',
                content: this.status ? '' + this.status : ''
            });
            const route = state ? state.route : undefined;
            if (route && route.data && route.data.rewrite) {
                add({
                    name: 'prerender-header',
                    content: 'Location:' + this.app.url.toUrl(route.data.rewrite)
                });
            }
            add({ name: 'display-size', content: this.client.size });
            add({
                name: 'model',
                content: model ? model.type + ':' + model.id : ''
            });
        } else {
            add({
                name: 'status-code',
                content: this.status ? '' + this.status : ''
            });
            if (this.client.mode === 'browser') {
                add({ name: 'fragment', content: '!' });
            }
        }

        const locale = this.app.multisite.current.contentLang || this.app.data.locale || 'en_GB';
        add({ name: 'Content-Language', content: locale });
        const searchParams = this.location.path().split('?')[1];
        let robots = state ? state.robots : '';
        if (searchParams) {
            const params = new URLSearchParams(searchParams);
            if (this.dfCoreOptions.meta.searchParamsForNoIndex.some(param => params.has(param))) {
                robots = 'NOINDEX, FOLLOW';
            }
        }
        add({ name: 'robots', content: robots });

        // site name
        if (this.app.multisite.current?.siteName) {
            add({ property: 'og:site_name', content: this.app.multisite.current?.siteName });
        }

        // change title
        if (!this.existingTitle) {
            this.existingTitle =
                (this.app.isPlatformBrowser && document.head.querySelector('title')) || this.renderer2.createElement('title');
            this.renderer2.appendChild(this.document.head, this.existingTitle);
        }

        const title = this.getMetaProperty('title') || this.getMetaProperty('name');
        this.renderer2.setProperty(this.existingTitle, 'innerText', title);

        if (model?.data) {
            const description = this.getMetaProperty('description');
            add({ name: 'description', content: description });
            add({ name: 'title', content: title });
            add({ name: 'keywords', content: this.getMetaProperty('keywords') });
            add({ property: 'og:type', content: this.getMetaProperty('ogtype') });
            add({ property: 'og:title', content: title });
            add({
                property: 'og:description',
                content: description ? description.replace(/<[^>]+>/gm, '') : description
            });
            // add({
            //     property: 'og:image',
            //     content: this.resizeService.resize(this.app.url.toMediaUrl(this.getMetaProperty('ogimage')
            // || this.getMetaProperty('image')), <IResizeOptions>{
            //         width: 500,
            //         height: 500,
            //         fitIn: true
            //     })
            // });
            add({
                property: 'og:image',
                content: this.app.url.toMediaUrl(this.getMetaProperty('ogimage') || this.getMetaProperty('image'))
            });
            add({ property: 'og:image:type', content: this.getMetaProperty('ogimageType') });
            add({ property: 'og:image:width', content: this.getMetaProperty('ogimageWidth') });
            add({ property: 'og:image:height', content: this.getMetaProperty('ogimageHeight') });
            add({ property: 'og:url', content: this.app.url.toUrl(this.getMetaProperty('url')) });
            add({ property: 'og:locale', content: locale });
            add({ name: 'twitter:url', content: this.app.url.toUrl(this.getMetaProperty('url')) });
            add({ name: 'twitter:title', content: this.getMetaProperty('title') || this.getMetaProperty('name') });
            add({ name: 'twitter:description', content: this.getMetaProperty('description') });
            add({
                name: 'twitter:image',
                content: this.app.url.toMediaUrl(this.getMetaProperty('ogimage') || this.getMetaProperty('image'))
            });

            if (model.type === 'product') {
                add({ property: 'og:price:amount', content: this.getMetaProperty('price') });
                add({
                    property: 'og:price:standard_amount',
                    content: this.getMetaProperty('org_price')
                });
                add({
                    property: 'og:price:currency',
                    content: this.app.data.currency.base
                });
                add({
                    property: 'og:availability',
                    content: this.getProductAvailability(<number>model.id, true)
                });
            }
        }

        // remove previous and set new ones
        difference(this.existingTags, tags).forEach(tag => this.metaService.removeTag('name="' + tag + '"'));
        this.existingTags = tags;

        // change canonical
        const canonical =
            (state &&
                (this.getMetaProperty('canonical', state.model || state.route?.target) ||
                    this.getMetaProperty('url', state.model || state.route?.target))) ||
            this.location.path().split('?')[0];
        if (canonical) {
            if (!this.existingCanonical) {
                this.existingCanonical =
                    (this.app.isPlatformBrowser && document.head.querySelector('link[rel="canonical"]')) ||
                    this.renderer2.createElement('link');
                this.renderer2.setAttribute(this.existingCanonical, 'rel', 'canonical');
                this.renderer2.appendChild(this.document.head, this.existingCanonical);
            }
            this.renderer2.setAttribute(this.existingCanonical, 'href', this.app.url.toUrl(canonical));
        } else {
            if (this.existingCanonical) {
                this.renderer2.removeChild(this.document.head, this.existingCanonical);
                delete this.existingCanonical;
            }
        }
    }

    /**
     * Retrieves product availability string
     */
    getProductAvailability(productId: number, fbSyntax: boolean = false): string | StructuralData.ItemAvailability {
        const product = this.app.resources.product.getExisting(productId);
        let stockStatus: number;
        if (!product) {
            return '';
        }
        if (product.isConfigurable || product.isBundle) {
            stockStatus = product.isOutOfStock ? 0 : 1;
        } else {
            const stockModel = product.stock;
            stockStatus = stockModel ? stockModel.checkStockStatus(1) : 0;
        }

        if (stockStatus > 0) {
            return fbSyntax ? 'instock' : StructuralData.ItemAvailability.inStock;
        }
        if (stockStatus < 0) {
            return fbSyntax ? 'instock' : StructuralData.ItemAvailability.preOrder;
        }
        return fbSyntax ? 'oos' : StructuralData.ItemAvailability.outOfStock;
    }

    /**
     * Retrieves proprty from meta object or model data
     */
    getMetaProperty(attr: string, model?: Model<IModelDataV3>): any {
        const state = this.state;
        if (model && !(state?.model === model)) {
            const data = model.rawData;
            return data && ((data.meta && data.meta[attr]) || data[attr]);
        }
        if (state) {
            if (state.metaData && state.metaData[attr] !== undefined) {
                return state.metaData[attr];
            }
            const md = this.model;
            if (md && md.rawData && md.rawData[attr]) {
                return md.rawData[attr];
            }
        }
        return '';
    }
}

declare module '@df/core/app' {
    interface App {
        meta: Meta;
    }
}
